import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import type { LocalVideoTrack } from "livekit-client";
// Type only, so the module itself is still loaded on demand further down.
import type { BackgroundProcessorWrapper } from "@livekit/track-processors";

import {
    BACKGROUND_STORAGE_KEY,
    BLUR_RADIUS,
    MEDIAPIPE_ASSET_PATHS,
    backgroundImageId,
    isBackgroundChoice,
    type BackgroundChoice,
} from "../domain/meetingBackground";
import { meetingBackgroundImageUrl } from "./useMeetingBackgrounds";
import { useRememberedChoice } from "./rememberedChoice";

export type BackgroundEffectChannel = {
    /** False where the browser cannot segment video at all. Hides the control. */
    supported: boolean;
    /** What is behind them: "none", "blur", or `image:<id>`. */
    choice: BackgroundChoice;
    blurred: boolean;
    /** The picture in use, or null when the choice is not a picture. */
    imageId: string | null;
    /** True while a choice is being applied. Segmenting takes a moment to warm up. */
    pending: boolean;
    /** True when the choice could not be applied and the camera is unchanged. */
    failed: boolean;
    choose: (choice: BackgroundChoice) => void;
    setBlurred: (blurred: boolean) => void;
};

/**
 * Whether this browser can run a background effect.
 *
 * The same capabilities `@livekit/track-processors` checks before it will
 * start. They are repeated here rather than imported because importing the
 * answer means downloading MediaPipe — around 150 KB of JavaScript before a
 * byte of WebAssembly — for every person who joins a call, including the ones
 * on a browser that was never going to be able to use it.
 *
 * It is a pre-filter, not the verdict: the library runs its own check when it
 * is actually loaded, and a failure there is reported rather than thrown.
 */
function canRunBackgroundEffects(): boolean {
    if (typeof window === "undefined" || typeof document === "undefined") return false;
    try {
        return (
            typeof OffscreenCanvas !== "undefined" &&
            typeof VideoFrame !== "undefined" &&
            typeof createImageBitmap !== "undefined" &&
            !!document.createElement("canvas").getContext("webgl2")
        );
    } catch {
        return false;
    }
}

/**
 * What is behind the person on this side of the call: nothing, a blur, or one
 * of their own pictures.
 *
 * The processor is attached to the published camera track, so what the other
 * person receives is already composed — there is no version of the raw room on
 * the wire, and no server involved in the compositing at any point. A picture
 * travels once, as a still, from our own API.
 *
 * The library is loaded on demand. It pulls in MediaPipe, and a call where
 * nobody touches the control should not pay for it.
 */
export function useBackgroundEffect(): BackgroundEffectChannel {
    const { cameraTrack } = useLocalParticipant();

    const [supported] = useState(canRunBackgroundEffects);
    const [choice, setChoice] = useRememberedChoice<BackgroundChoice>(
        BACKGROUND_STORAGE_KEY,
        isBackgroundChoice,
        "none",
        supported
    );
    const [pending, setPending] = useState(false);
    const [failed, setFailed] = useState(false);

    /** The processor in use, the track it is attached to, and what it draws. */
    const processor = useRef<BackgroundProcessorWrapper | null>(null);
    const attachedTo = useRef<LocalVideoTrack | null>(null);
    const applied = useRef<BackgroundChoice | null>(null);
    /**
     * The blob URL the picture is being drawn from.
     *
     * Held in a ref rather than in state because it is a resource, not a
     * rendering: every one of these pins its bytes in memory until it is
     * revoked, and a therapist trying four pictures in a row would otherwise
     * leave four images behind for the length of the call.
     */
    const imageUrl = useRef<string | null>(null);

    const blurred = choice === "blur";
    const imageId = backgroundImageId(choice);
    const track = (cameraTrack?.track ?? null) as LocalVideoTrack | null;

    const releaseImage = useCallback(() => {
        if (!imageUrl.current) return;
        URL.revokeObjectURL(imageUrl.current);
        imageUrl.current = null;
    }, []);

    const apply = useCallback(
        async (video: LocalVideoTrack, next: BackgroundChoice) => {
            setPending(true);
            setFailed(false);
            try {
                if (next === "none") {
                    // Ask the track, not our own bookkeeping. A ref saying what
                    // we last attached is a second source of truth, and the
                    // moment it drifts — a republished camera, a component that
                    // came back — the effect stays on a track we have stopped
                    // believing we touched, with no way for anyone to take it
                    // off.
                    if (video.getProcessor()) await video.stopProcessor();
                    processor.current = null;
                    attachedTo.current = null;
                    applied.current = null;
                    releaseImage();
                    return;
                }

                const processors = await import("@livekit/track-processors");
                if (!processors.supportsBackgroundProcessors()) {
                    setFailed(true);
                    return;
                }

                // Already drawing exactly this, on exactly this track.
                if (
                    video.getProcessor() &&
                    attachedTo.current === video &&
                    applied.current === next
                ) {
                    return;
                }

                let options;
                const id = backgroundImageId(next);
                if (id) {
                    let url: string;
                    try {
                        url = await meetingBackgroundImageUrl(id);
                    } catch {
                        // Deleted while it was remembered, or it was the
                        // centre's and the therapist has left. Nothing went
                        // wrong that they did: the choice quietly becomes
                        // "none", which is what the effect will then apply.
                        setChoice("none");
                        return;
                    }
                    releaseImage();
                    imageUrl.current = url;
                    options = {
                        mode: "virtual-background" as const,
                        imagePath: url,
                        assetPaths: MEDIAPIPE_ASSET_PATHS,
                    };
                } else {
                    releaseImage();
                    options = {
                        mode: "background-blur" as const,
                        blurRadius: BLUR_RADIUS,
                        assetPaths: MEDIAPIPE_ASSET_PATHS,
                    };
                }

                const processorInstance = processors.BackgroundProcessor(options);
                await video.setProcessor(processorInstance);
                processor.current = processorInstance;
                attachedTo.current = video;
                applied.current = next;
            } catch {
                // The camera keeps working; only the background is missing, and
                // the control says so rather than leaving someone believing
                // they are covered when they are not.
                setFailed(true);
                processor.current = null;
                attachedTo.current = null;
                applied.current = null;
                releaseImage();
            } finally {
                setPending(false);
            }
        },
        [releaseImage, setChoice]
    );

    /**
     * Apply the choice, and re-apply it to whatever track the camera is on.
     *
     * Turning the camera off and on again publishes a *new* track with no
     * processor attached, so without this the background would silently fall
     * away the first time someone covered their camera mid session.
     */
    useEffect(() => {
        if (!supported) return;
        if (!track) {
            // Nothing to attach to yet, and nothing left attached either.
            processor.current = null;
            attachedTo.current = null;
            applied.current = null;
            return;
        }
        if (choice === "none" && attachedTo.current === null) return;
        void apply(track, choice);
    }, [apply, choice, supported, track]);

    /**
     * The picture does not outlive the call.
     *
     * Unmounting takes the processor with it, but the blob URL is the
     * browser's to hold until somebody revokes it — and nobody else will.
     */
    useEffect(() => () => releaseImage(), [releaseImage]);

    const setBlurred = useCallback(
        (next: boolean) => setChoice(next ? "blur" : "none"),
        [setChoice]
    );

    return {
        supported,
        choice,
        blurred,
        imageId,
        pending,
        failed,
        choose: setChoice,
        setBlurred,
    };
}
